import { useEffect, useRef } from "react";

// Scroll plumbing for the Create League flow.
//
// The one thing to know before touching any of this: the app does NOT scroll the
// window. `.adminLayout` is overflow-hidden and `.mainWrapper` is
// overflow-y-auto (see index.css), so `.mainWrapper` is the scroller for every
// screen under it. `window.scrollTo(0, 0)` is therefore a no-op here, which is
// why the client saw steps opening halfway down the page.

// The nearest ancestor that actually scrolls. Falls back to the document for
// anything mounted outside the app layout (tests, storybook).
export const getScrollParent = (node) => {
  let el = node?.parentElement || null;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

// Put a screen at its true top. `smooth` is for a step change the user asked
// for; a fresh mount jumps, because animating from a position the user never
// chose just shows them the wrong place first.
export const scrollToTop = (node, smooth = false) => {
  const scroller = getScrollParent(node);
  const behavior = smooth ? "smooth" : "auto";
  scroller.scrollTo({ top: 0, behavior });
  // Belt and braces: some screens under /contest scroll the document instead
  // (no app layout, e.g. a direct deep link before hydration settles).
  window.scrollTo({ top: 0, behavior });
};

// Client Rev 1: "On input focus, automatically scroll the active field above the
// keyboard. This is an issue and hard to type, so it gets annoying." — the
// browser's own scroll-into-view measures against the LAYOUT viewport, which
// still includes the area the keyboard now covers, so a field near the bottom
// (stock search, League link) stays hidden behind it.
//
// This hook measures against `visualViewport` — the part actually visible — and
// scrolls the field into the gap between the fixed flow header and the top of
// the keyboard.
//
// Why the retries: the keyboard animates in over ~300ms and browsers are
// inconsistent about when (and whether) they fire `visualViewport.resize`. So we
// re-measure on every resize AND on a short ladder of timers, and stop as soon
// as the field is comfortably in view.
//
// `scrollSelf` is for a container that is ITSELF the scroller — a dialog panel
// with `overflow-y-auto`, as opposed to the Create League flow whose container
// is a plain block inside `.mainWrapper`. getScrollParent deliberately starts at
// `node.parentElement`, so for such a panel it walks straight past the only
// element that can actually scroll and lands on the document. Scrolling the
// document moves nothing when the dialog is `position: fixed`, which is exactly
// why the keyboard kept covering the field (client Rev 5).
export const useKeyboardAwareFocus = (
  containerRef,
  { headerOffset = 64, gap = 16, enabled = true, scrollSelf = false } = {}
) => {
  const focusedRef = useRef(null);
  const timersRef = useRef([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container) return undefined;

    const clearTimers = () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };

    const bringIntoView = () => {
      const el = focusedRef.current;
      if (!el || !el.isConnected) return;
      const vv = window.visualViewport;
      // The band the user can actually see and type in: below the fixed flow
      // header, above whatever the keyboard is covering.
      const visibleTop = headerOffset + gap;
      const visibleBottom = (vv ? vv.height : window.innerHeight) - gap;
      const rect = el.getBoundingClientRect();

      // Push up far enough to clear the keyboard...
      let delta = rect.bottom > visibleBottom ? rect.bottom - visibleBottom : 0;
      // ...but never so far that the field's own top disappears under the
      // header. A field taller than the band (the rich-text Description) is
      // anchored by its top, which is where the caret starts.
      if (rect.top - delta < visibleTop) delta = rect.top - visibleTop;
      if (Math.abs(delta) < 2) return;

      // Instant, not smooth: this runs again on every viewport resize and on a
      // short retry ladder, and a smooth scroll still in flight measures from a
      // stale position, which is how the field ends up overshot.
      const scroller = scrollSelf ? container : getScrollParent(container);
      scroller.scrollBy({ top: delta, behavior: "auto" });
    };

    const onFocusIn = (e) => {
      const el = e.target?.closest?.(
        'input, textarea, select, [contenteditable="true"], .ql-editor'
      );
      if (!el) return;
      focusedRef.current = el;
      clearTimers();
      // 0ms catches a focus with no keyboard (desktop, a date picker); the rest
      // catch it once the keyboard has finished animating.
      timersRef.current = [0, 150, 350, 600].map((ms) =>
        setTimeout(bringIntoView, ms)
      );
    };

    const onFocusOut = () => {
      focusedRef.current = null;
      clearTimers();
    };

    const onViewportChange = () => {
      if (focusedRef.current) bringIntoView();
    };

    container.addEventListener("focusin", onFocusIn);
    container.addEventListener("focusout", onFocusOut);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", onViewportChange);
    vv?.addEventListener("scroll", onViewportChange);

    return () => {
      clearTimers();
      container.removeEventListener("focusin", onFocusIn);
      container.removeEventListener("focusout", onFocusOut);
      vv?.removeEventListener("resize", onViewportChange);
      vv?.removeEventListener("scroll", onViewportChange);
    };
  }, [containerRef, headerOffset, gap, enabled, scrollSelf]);
};

// Client Rev 1: the creation flow runs focused — no bottom app navigation, and
// the sticky CTA sits at the true bottom of the screen with the whole button
// visible. `.adminLayout` is sized 100vh-4rem on mobile to leave room for that
// navigation, so while the flow is open the body carries this class and index.css
// gives the layout the full height back.
// `active` is false when the wizard is editing an existing league instead of
// creating one — that screen keeps the normal app chrome, so it must not claim
// the full-height layout.
export const useFocusedFlowChrome = (active = true) => {
  useEffect(() => {
    if (!active) return undefined;
    document.body.classList.add("league-create-flow");
    return () => document.body.classList.remove("league-create-flow");
  }, [active]);
};
